import dayjs from 'dayjs'
import React from 'react'
import { Link } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { calculateBusinessDays, formatDateTime } from '@/utils/formatters'

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  })

// Mirrors REPAIR_ORDER_STATUS on the backend; `id` must match the stored value.
export const STATUS_OPTIONS = [
  { id: 'Not Started', label: 'Not Started' },
  { id: 'On Outstanding List', label: 'On Outstanding List' },
  { id: 'Closed', label: 'Closed' },
  { id: 'Closed — Submitted', label: 'Closed — Submitted' },
  { id: 'Written Off', label: 'Written Off' },
  { id: 'Paid', label: 'Paid' },
  { id: 'Paid with Adjustments', label: 'Paid with Adjustments' },
]

// Mirrors REPAIR_ORDER_STATE on the backend (outstanding_list_details.state).
export const OL_STATE_OPTIONS = [
  { id: 'Draft', label: 'Draft' },
  { id: 'WPC Review', label: 'WPC Review' },
  { id: 'Dealer Review', label: 'Dealer Review' },
  { id: 'Resolved', label: 'Resolved' },
]

export const COLUMNS = [
  {
    id: 'ro_number',
    name: 'RO #',
    selector: (row) => row.roNumber,
    cell: (row) => (
      <Link
        to={`/dealer/${row.dealer?.id}/repair-order/${row.id}`}
        className="hover:text-blue-dark text-md truncate text-blue-800"
      >
        {row.roNumber}
      </Link>
    ),
    sortable: true,
    sortBy: 'ro_number',
    width: '140px',
  },
  {
    id: 'dealer',
    name: 'Dealer',
    selector: (row) => row.dealer?.name,
    cell: (row) => <span className="truncate">{row.dealer?.name || 'N/A'}</span>,
    sortable: true,
    sortBy: 'dealer',
    minWidth: '200px',
  },
  {
    id: 'status',
    name: 'Status',
    selector: (row) => row.status,
    sortable: true,
    sortBy: 'status',
    width: '190px',
  },
  {
    id: 'state',
    name: 'OL Status',
    selector: (row) => row.state,
    cell: (row) => <span className="truncate">{row.state || 'N/A'}</span>,
    sortable: true,
    sortBy: 'state',
    width: '160px',
  },
  {
    id: 'received_at',
    name: 'Received',
    selector: (row) => row.receivedAt,
    format: (row) => (row.receivedAt ? dayjs(row.receivedAt).format('MM/DD/YYYY') : 'N/A'),
    sortable: true,
    sortBy: 'received_at',
    width: '130px',
  },
  {
    id: 'total_unresolved_claim_amount',
    name: 'Amount',
    selector: (row) => row.totalUnresolvedClaimAmount,
    cell: (row) => <span>{formatCurrency(row.totalUnresolvedClaimAmount)}</span>,
    sortable: true,
    sortBy: 'total_unresolved_claim_amount',
    width: '120px',
  },
  {
    id: 'advisor',
    name: 'ADV ID',
    selector: (row) => row.advisor,
    cell: (row) => <span className="truncate">{row.advisor || 'N/A'}</span>,
    sortable: true,
    sortBy: 'advisor',
    width: '140px',
  },
  {
    id: 'technicians',
    name: 'Tech #',
    selector: (row) => row.technicians,
    cell: (row) => <span className="truncate">{(row.technicians || []).join(', ') || 'N/A'}</span>,
    sortable: false,
    width: '140px',
  },
  {
    id: 'last_updated_on',
    name: 'Last Updated',
    selector: (row) => row.lastUpdatedOn,
    // Match the per-dealer RO list: flag ROs untouched for 2+ business days.
    cell: (row) => (
      <span
        className={mergeClassNames(
          calculateBusinessDays(row.lastUpdatedOn, new Date()) >= 2 && 'text-red font-semibold',
        )}
      >
        {formatDateTime(row.lastUpdatedOn)}
      </span>
    ),
    sortable: true,
    sortBy: 'last_updated_on',
    width: '190px',
  },
  {
    id: 'days_on_list',
    name: 'Days On List',
    selector: (row) => row.daysOnList,
    format: (row) =>
      row.daysOnList || row.daysOnList === 0
        ? `${row.daysOnList} day${row.daysOnList === 1 ? '' : 's'}`
        : 'N/A',
    sortable: true,
    sortBy: 'days_on_list',
    width: '150px',
  },
]
